import { createLayerCanvas, resizeLayerCanvas, replaceLayerCanvas } from "../layerCanvas";
import { createCanvasSurface } from "../canvas2d/canvasSurface";
import { createWebglSurface } from "./webglSurface";

/**
 * Bottom canvas layer for all chart geometry. Draws with WebGL2
 * (webglSurface); without WebGL2, when its shaders do not build or a lost
 * context does not come back, the layer moves to a new canvas with
 * Canvas 2D (canvasSurface) and the same renderers paint there (plan §1).
 *
 * @param {HTMLElement} container
 * @param {object} params
 * @param {Array<{ init: Function }>} params.renderers - line, rect and band renderers;
 *   more come and go with indicators (addRenderer, removeRenderer)
 * @param {() => void} params.onRestored - called when the layer can draw again
 */
export const createGlLayer = (container, { renderers: initialRenderers, onRestored }) => {
  const renderers = [...initialRenderers];
  let canvas = createLayerCanvas(container);
  let size = null;
  let surface = null;

  const switchToCanvas2d = () => {
    surface?.destroy();
    canvas = replaceLayerCanvas(canvas);

    if (size) resizeLayerCanvas(canvas, size);

    surface = createCanvasSurface(canvas, renderers);
  };

  surface = createWebglSurface(canvas, {
    renderers,
    onRestored,
    onRestoreFailed: () => {
      switchToCanvas2d();
      onRestored();
    },
  });

  if (!surface) switchToCanvas2d();

  return {
    /** False while the WebGL context is lost and not yet given up. */
    get isReady() {
      return surface.isReady;
    },

    /** [width, height] of the drawing buffer in device pixels. */
    get viewport() {
      return [canvas.width, canvas.height];
    },

    resize: (nextSize) => {
      size = nextSize;
      resizeLayerCanvas(canvas, nextSize);
    },

    /** @param {{ init: Function }} renderer - drawn from now on, also after a context switch */
    addRenderer: (renderer) => {
      renderers.push(renderer);
      surface.initRenderer(renderer);
    },

    /** @param {{ release: Function }} renderer - added by addRenderer, its buffers are freed */
    removeRenderer: (renderer) => {
      const index = renderers.indexOf(renderer);

      if (index >= 0) renderers.splice(index, 1);

      renderer.release();
    },

    /** @param {number[]} background - RGBA in 0..1 */
    beginFrame: (background) => surface.beginFrame(background),

    /** @param {{ x: number, y: number, width: number, height: number }} rect - device px from the top-left */
    clipTo: (rect) => surface.clipTo(rect),

    /** @param {number[]} color - RGBA in 0..1, fills the rectangle of the last clipTo */
    fillClip: (color) => surface.fillClip(color),

    destroy: () => {
      surface.destroy();
      canvas.remove();
    },
  };
};
